import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  HostListener,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Validators } from '@angular/forms';
import {
  UiButtonComponent,
  UiFormComponent,
  UiPanelComponent,
  UiTabGroupComponent,
} from '@template-ui/components';
import { UiButtonDirective } from '@template-ui/directives';
import type { UiNavigationTab } from '@template-ui/types';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';
import {
  WorkflowDefinitionDocument,
  WorkflowStatusConfiguration,
  WorkflowStatusRule,
} from './platform-workflow-definition-lifecycle.models';
import {
  emptyStatusConfiguration,
  replaceStatusConfiguration,
  statusConfigurationAt,
  statusConfigurationError,
  statusEvents,
  statusRequirementLabel,
  statusRequirementProvider,
  statusRuleError,
  statusScopeNode,
  statusStepOutcomeChoices,
} from './workflow-status-authoring';
import { WorkflowEditorPendingScope, WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

/** The two compact views of one workflow or visit-owned status configuration. */
export type WorkflowStatusWorkspaceView = 'labels' | 'changes';
/** Origin witness for the dedicated workflow-settings owner; it is never a wire contract. */
export interface WorkflowStatusOwnedDocumentChange {
  document: WorkflowDefinitionDocument;
  nodeId: string | null;
  owner: string;
  source: string;
  view: WorkflowStatusWorkspaceView;
}
interface StatusFormOrigin { kind: 'name' | 'rule' | 'settings'; generation: number; view: WorkflowStatusWorkspaceView; target: string | null; config: InputConfig[]; }
interface StatusInspectionOrigin {
  generation: number; editorGeneration: number; owner: string; source: string; presentation: string;
  selection: string; view: WorkflowStatusWorkspaceView;
}
interface StatusRuleNavigation extends StatusInspectionOrigin {
  statusCode: string; statusValue: string; ruleId: string; ruleValue: string;
}
interface StatusLabelReturn { statusCode: string; ruleId: string; label: string; presentation: string; }
interface StatusLabelReturnNavigation extends StatusInspectionOrigin { target: StatusLabelReturn; }

@Component({
  selector: 'phms-workflow-status-editor',
  standalone: true,
  imports: [CommonModule, UiButtonComponent, UiFormComponent, UiPanelComponent, UiTabGroupComponent, UiButtonDirective],
  templateUrl: './workflow-status-editor.component.html',
  styleUrl: './workflow-status-editor.component.scss',
})
export class WorkflowStatusEditorComponent implements OnChanges {
  private static nextInstanceId = 0;
  private readonly instanceId = `workflow-status-${++WorkflowStatusEditorComponent.nextInstanceId}`;
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() nodeId: string | null = null;
  @Input() canAuthor = false;
  @Input() interactionBlocked = false;
  @Input() resetVersion = 0;
  @Input() immediateChanges = false;
  @Input() workspaceMode = false;
  @Input() stepWorkspaceMode = false;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() viewContext: (() => unknown) | null = null;
  @Input() active: (() => boolean) | null = null;
  @Input() activeView: (() => string | null) | null = null;
  @Input() activeViewState: string | null | undefined = undefined;
  @Output() readonly documentChange =
    new EventEmitter<WorkflowDefinitionDocument>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();
  @Output() readonly ownedDocumentChange = new EventEmitter<WorkflowStatusOwnedDocumentChange>();
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });
  private readonly destroy = inject(DestroyRef);
  config = emptyStatusConfiguration();
  settingsValues = { initialStatusCode: '', allowManualChanges: false };
  settingsConfig: InputConfig[] = [];
  nameValues = { label: '' };
  nameConfig: InputConfig[] = [
    {
      name: 'label',
      label: 'Status name',
      type: EzFormInputType.TEXT,
      width: '1x',
      validation: [Validators.required, Validators.maxLength(120)],
      placeholder: 'For example: Awaiting documents',
    },
  ];
  nameEditing = false;
  editingCode: string | null = null;
  ruleValues = {
    eventCode: '',
    statusCode: '',
    sourceNodeId: '',
    requirementId: '',
    outcomeCode: '',
  };
  ruleConfig: InputConfig[] = [];
  ruleEditing = false;
  editingRuleId: string | null = null;
  error = '';
  message = '';
  pending = false;
  private baseline = '';
  private baselineReference = '';
  private context = '';
  private applying = false;
  private pendingBeforeName = false;
  private errorBeforeName = '';
  private pendingBeforeRule = false;
  private errorBeforeRule = '';
  private ownerSnapshot = '';
  private registeredOwner = false;
  private bufferPresentationKey = '';
  private renderedActiveView: string | null = null;
  private presentationKey = '';
  private selectedStatusCode: string | undefined;
  private selectedRuleId: string | undefined;
  private compactView: WorkflowStatusWorkspaceView = 'labels';
  private editorGeneration = 0;
  private inspectionGeneration = 0;
  private statusLabelReturn: StatusLabelReturn | null = null;
  private removalRefusal: { code: string; statusValue: string; rulesValue: string; message: string } | null = null;
  private focusRequest = 0;
  retainedIssue = '';
  retainedConfiguration: unknown = null;

  constructor() { this.destroy.onDestroy(() => { this.rememberSelection(); this.focusRequest++; }); }
  get compactWorkspace(): boolean { return this.nodeId === null ? this.workspaceMode : this.stepWorkspaceMode; }
  formInstanceId(kind: 'settings' | 'name' | 'rule'): string {
    return `${this.instanceId}-${this.nodeId === null ? 'workflow' : `step-${encodeURIComponent(this.nodeId)}`}-${kind}`;
  }
  get headingId(): string { return `${this.instanceId}-${this.nodeId === null ? 'workflow' : `step-${encodeURIComponent(this.nodeId)}`}-title`; }
  get behaviorHeadingId(): string { return `${this.headingId}-behavior`; }
  get eventsHeadingId(): string { return `${this.headingId}-events`; }
  get workspaceView(): WorkflowStatusWorkspaceView { return this.compactView; }
  get workspaceTabs(): UiNavigationTab[] { return [
    { id: 'labels', label: this.nodeId === null ? 'Status labels' : 'Step labels', badge: String(this.config.statuses.length), disabled: this.interactionBlocked || this.nameEditing || this.ruleEditing },
    { id: 'changes', label: 'Automatic changes', badge: String(this.config.rules.length), disabled: this.interactionBlocked || this.nameEditing || this.ruleEditing },
  ]; }
  get selectedStatus() { const rows = this.config.statuses.filter(status => status.code === this.selectedStatusCode); return rows.length === 1 ? rows[0] : null; }
  get selectedRule() { const rows = this.config.rules.filter(rule => rule.id === this.selectedRuleId); return rows.length === 1 ? rows[0] : null; }
  get selectedStatusRules(): WorkflowStatusRule[] { return this.selectedStatus ? this.config.rules.filter(rule => rule.statusCode === this.selectedStatus!.code) : []; }
  get selectedStatusUsage() {
    const status = this.selectedStatus;
    if (!status) return [];
    const origin = this.inspectionOrigin();
    return this.selectedStatusRules.map(rule => ({ rule, summary: this.ruleSummary(rule), navigation: {
      ...origin, statusCode: status.code, statusValue: JSON.stringify(status), ruleId: rule.id, ruleValue: JSON.stringify(rule),
    } satisfies StatusRuleNavigation }));
  }
  get statusRemovalMessage(): string {
    const status = this.selectedStatus, refusal = this.removalRefusal;
    return status && refusal?.code === status.code && refusal.statusValue === JSON.stringify(status)
      && refusal.rulesValue === JSON.stringify(this.selectedStatusRules) ? refusal.message : '';
  }
  get automaticChangeReturnView() {
    const target = this.statusLabelReturn;
    if (!target || !this.compactWorkspace || this.compactView !== 'changes' || target.presentation !== this.presentationKey
      || this.presentationKey !== this.selectionKey()) return null;
    const status = this.returnStatus(target);
    return { label: `Back to ${status?.label || target.label || 'status label'}`, available: !!status,
      navigation: { ...this.inspectionOrigin(), target } satisfies StatusLabelReturnNavigation };
  }
  get nameEditorView() { return { config: this.nameConfig, origin: this.formOrigin('name') }; }
  get ruleEditorView() { return { config: this.ruleConfig, origin: this.formOrigin('rule') }; }
  get settingsEditorView() { return { config: this.settingsConfig, origin: this.formOrigin('settings') }; }

  get disabled(): boolean {
    return (
      !this.canAuthor ||
      this.interactionBlocked ||
      !this.document ||
      !!this.retainedIssue || !this.ownerCurrent() ||
      (this.nodeId !== null && !statusScopeNode(this.document, this.nodeId))
    );
  }
  get title(): string {
    return this.nodeId === null ? 'Workflow statuses' : 'Optional step labels';
  }
  get scopeLabel(): string {
    return this.nodeId === null
      ? this.document?.displayName || 'this workflow'
      : this.document
        ? statusScopeNode(this.document, this.nodeId)?.displayName ||
          'this step'
        : 'this step';
  }

  ngOnChanges(changes: SimpleChanges = {}): void {
    const firstOwner = !!this.ownerContext && !this.registeredOwner;
    const activeView = this.activeView?.() ?? null;
    const matchingView = this.activeViewState === undefined || this.activeViewState === activeView;
    if ((!this.activeView || matchingView) && this.renderedActiveView !== activeView) {
      this.renderedActiveView = activeView; this.editorGeneration++; this.inspectionGeneration++; this.focusRequest++;
    }
    if (!this.pending && this.activeView && activeView !== null && this.activeViewState === activeView && changes['activeViewState']) {
      this.ownerSnapshot = this.ownerKey(); this.registeredOwner ||= !!this.ownerContext;
    }
    const context = `${this.document?.workflowCode}/${this.document?.definitionVersionCode}/${this.nodeId}/${this.resetVersion}`;
    if (this.pending && changes['document'] && this.viewContext && this.bufferPresentationKey === this.selectionKey() && context === this.context
      && JSON.stringify([this.document?.workflowCode, this.document?.definitionVersionCode, this.document?.packReference]) === this.baselineReference
      && JSON.stringify(this.rawConfiguration(this.document)) === this.baseline) this.ownerSnapshot = this.ownerKey();
    if (context !== this.context || (!this.pending && changes['document'])) {
      this.context = context;
      this.ownerSnapshot = this.ownerKey(); this.registeredOwner ||= !!this.ownerContext;
      this.reset();
    } else if (firstOwner && !this.pending) { this.ownerSnapshot = this.ownerKey(); this.registeredOwner = true; }
    else if (changes['immediateChanges']) this.rebuildSettings();
    if (changes['document'] && this.ruleEditing && !this.retainedIssue) this.rebuildRuleForm();
    this.reconcilePresentation();
  }

  private ownerKey(): string { return JSON.stringify(this.ownerContext?.() ?? null); }
  private currentDocument(): WorkflowDefinitionDocument | null { return this.latestDocument ? this.latestDocument() : this.document; }
  private ownerCurrent(): boolean { return !this.destroy.destroyed && this.ownerKey() === this.ownerSnapshot; }
  private documentKey(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document?.workflowCode, document?.definitionVersionCode, document?.packReference, this.rawConfiguration(document),
      document?.capabilityReferences, Array.isArray(document?.nodes) ? document.nodes.map(node => [node?.id, node?.typeCode, node?.declaration?.behavior]) : document?.nodes, document?.edges]);
  }
  private presentationActive(): boolean {
    return this.ownerCurrent() && (!this.active || this.active()) && !this.interactionBlocked && !this.ownerViews?.focusSuspended()
      && (!this.activeView || this.renderedActiveView !== null && this.renderedActiveView === this.activeView()
        && (this.activeViewState === undefined || this.activeViewState === this.renderedActiveView))
      && !this.element.nativeElement.closest('[hidden],[inert]');
  }
  private nativeCurrent(view?: WorkflowStatusWorkspaceView, origin?: StatusFormOrigin): boolean {
    return this.presentationActive() && (!this.compactWorkspace || !view || this.compactView === view)
      && this.documentKey(this.document) === this.documentKey(this.currentDocument())
      && JSON.stringify(this.rawConfiguration(this.currentDocument())) === this.baseline
      && (!origin || origin.generation === this.editorGeneration && origin.view === this.compactView
        && origin.target === (origin.kind === 'rule' ? this.editingRuleId : origin.kind === 'name' ? this.editingCode : null)
        && origin.config === (origin.kind === 'rule' ? this.ruleConfig : origin.kind === 'name' ? this.nameConfig : this.settingsConfig));
  }
  private formOrigin(kind: 'name' | 'rule' | 'settings'): StatusFormOrigin {
    return { kind, generation: this.editorGeneration, view: this.compactView, target: kind === 'rule' ? this.editingRuleId : kind === 'name' ? this.editingCode : null,
      config: kind === 'rule' ? this.ruleConfig : kind === 'name' ? this.nameConfig : this.settingsConfig };
  }
  private inspectionOrigin(): StatusInspectionOrigin {
    return { generation: this.inspectionGeneration, editorGeneration: this.editorGeneration, owner: this.ownerKey(),
      source: this.documentKey(this.currentDocument()), presentation: this.presentationKey,
      selection: JSON.stringify([this.selectedStatusCode, this.selectedRuleId]), view: this.compactView };
  }
  private inspectionCurrent(origin: StatusInspectionOrigin, view: WorkflowStatusWorkspaceView): boolean {
    return this.compactWorkspace && !this.retainedIssue && !this.nameEditing && !this.ruleEditing && this.nativeCurrent(view)
      && origin.generation === this.inspectionGeneration && origin.editorGeneration === this.editorGeneration && origin.view === view
      && origin.owner === this.ownerKey() && origin.source === this.documentKey(this.currentDocument())
      && origin.presentation === this.presentationKey && this.presentationKey === this.selectionKey()
      && origin.selection === JSON.stringify([this.selectedStatusCode, this.selectedRuleId]);
  }
  private returnStatus(target: StatusLabelReturn) {
    const saved = this.rawConfiguration(this.currentDocument()) as WorkflowStatusConfiguration | null;
    if (!Array.isArray(saved?.statuses)) return null;
    const rows = this.config.statuses.filter(status => status.code === target.statusCode);
    return rows.length === 1 && saved.statuses.filter(status => status?.code === target.statusCode).length === 1 ? rows[0] : null;
  }
  /** Inspect one exact saved dependency without creating an edit candidate or settling input. */
  reviewAutomaticChange(request: StatusRuleNavigation): boolean {
    if (!request || !this.inspectionCurrent(request, 'labels') || request.statusCode !== this.selectedStatusCode) return false;
    const status = this.selectedStatus, rules = this.config.rules.filter(rule => rule.id === request.ruleId);
    const saved = this.rawConfiguration(this.currentDocument()) as WorkflowStatusConfiguration | null;
    if (!status || JSON.stringify(status) !== request.statusValue || rules.length !== 1 || rules[0].statusCode !== status.code
      || JSON.stringify(rules[0]) !== request.ruleValue || !Array.isArray(saved?.rules) || !Array.isArray(saved?.statuses)
      || saved.rules.filter(rule => rule?.id === request.ruleId && JSON.stringify(rule) === request.ruleValue).length !== 1
      || saved.rules.filter(rule => rule?.id === request.ruleId).length !== 1
      || saved.statuses.filter(row => row?.code === status.code).length !== 1) return false;
    this.statusLabelReturn = { statusCode: status.code, ruleId: request.ruleId, label: status.label, presentation: this.presentationKey };
    this.inspectionGeneration++; this.compactView = 'changes'; this.selectedRuleId = request.ruleId;
    this.rememberSelection();
    this.queueFocus(host => Array.from(host.querySelectorAll<HTMLElement>('[data-status-rule]'))
      .find(row => row.getAttribute('data-status-rule') === request.ruleId) ?? null, true);
    return true;
  }
  /** Return to the originating immutable label, even after its dependency was deliberately removed. */
  backToStatusLabel(request: StatusLabelReturnNavigation): boolean {
    if (!request?.target || !this.inspectionCurrent(request, 'changes') || request.target !== this.statusLabelReturn
      || request.target.presentation !== this.presentationKey || !this.returnStatus(request.target)) return false;
    const { statusCode, ruleId } = request.target;
    this.inspectionGeneration++; this.compactView = 'labels'; this.selectedStatusCode = statusCode; this.statusLabelReturn = null;
    this.rememberSelection();
    this.queueFocus(host => {
      const detail = Array.from(host.querySelectorAll<HTMLElement>('[data-status-label-detail]'))
        .find(row => row.getAttribute('data-status-label-detail') === statusCode);
      return detail && Array.from(detail.querySelectorAll<HTMLElement>('[data-status-usage-rule]'))
        .find(row => row.getAttribute('data-status-usage-rule') === ruleId)
        || Array.from(host.querySelectorAll<HTMLElement>('[data-status-label]')).find(row => row.getAttribute('data-status-label') === statusCode) || null;
    }, true);
    return true;
  }
  private rawConfiguration(document: WorkflowDefinitionDocument | null): unknown {
    if (!document) return null;
    try { return statusConfigurationAt(document, this.nodeId); } catch { return { unavailableStatusOwner: true }; }
  }
  private configurationShapeIssue(value: unknown): string {
    if (value == null) return '';
    const record = (item: unknown): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item);
    if (!record(value) || !Array.isArray(value['statuses']) || !Array.isArray(value['rules'])
      || typeof value['initialStatusCode'] !== 'string' || typeof value['allowManualChanges'] !== 'boolean'
      || 'version' in value || 'contractVersion' in value) return 'These saved status settings contain unsupported content. They are preserved read-only.';
    if (value['statuses'].some(status => !record(status) || typeof status['code'] !== 'string' || !status['code'].trim() || typeof status['label'] !== 'string')
      || value['rules'].some(rule => !record(rule) || typeof rule['id'] !== 'string' || !rule['id'].trim() || typeof rule['eventCode'] !== 'string'
        || typeof rule['statusCode'] !== 'string' || ['sourceNodeId', 'requirementId', 'outcomeCode'].some(key => rule[key] != null && typeof rule[key] !== 'string')))
      return 'Saved status rows or automatic changes are unavailable for editing. Their original values are preserved.';
    if (new Set(value['statuses'].map(status => status['code'])).size !== value['statuses'].length || new Set(value['rules'].map(rule => rule['id'])).size !== value['rules'].length)
      return 'Saved status or automatic-change identities are ambiguous. Their original configuration is preserved read-only.';
    return '';
  }
  private selectionKey(): string {
    return this.document ? JSON.stringify([this.ownerViews?.key(this.document, this.nodeId ?? 'workflow', 'status-workspace'), this.viewContext?.() ?? this.ownerContext?.() ?? null]) : '';
  }
  private rememberSelection(): void {
    if (this.presentationKey) this.ownerViews?.remember(this.presentationKey, { view: this.compactView, status: this.selectedStatusCode, rule: this.selectedRuleId });
  }
  private reconcilePresentation(): void {
    const key = this.selectionKey();
    if (key !== this.presentationKey) {
      this.rememberSelection(); this.presentationKey = key;
      this.statusLabelReturn = null; this.removalRefusal = null; this.inspectionGeneration++;
      const saved = this.ownerViews?.read<{ view: WorkflowStatusWorkspaceView; status?: string; rule?: string }>(key);
      this.compactView = saved?.view ?? 'labels'; this.selectedStatusCode = saved?.status; this.selectedRuleId = saved?.rule; this.focusRequest++;
    }
    if (this.selectedStatusCode === undefined) this.selectedStatusCode = this.config.statuses[0]?.code;
    if (this.selectedRuleId === undefined) this.selectedRuleId = this.config.rules[0]?.id;
  }
  selectWorkspaceView(value: string): void {
    if (!this.presentationActive() || this.nameEditing || this.ruleEditing || value !== 'labels' && value !== 'changes') return;
    this.inspectionGeneration++; this.statusLabelReturn = null;
    this.focusRequest++; this.compactView = value; this.rememberSelection();
  }
  selectStatus(code: string): void {
    if (!this.nativeCurrent('labels') || this.nameEditing || this.ruleEditing || this.config.statuses.filter(status => status.code === code).length !== 1) return;
    this.inspectionGeneration++; this.statusLabelReturn = null;
    this.focusRequest++; this.selectedStatusCode = code; this.rememberSelection();
  }
  selectRule(id: string): void {
    if (!this.nativeCurrent('changes') || this.nameEditing || this.ruleEditing || this.config.rules.filter(rule => rule.id === id).length !== 1) return;
    this.inspectionGeneration++;
    if (id !== this.selectedRuleId) this.statusLabelReturn = null;
    this.focusRequest++; this.selectedRuleId = id; this.rememberSelection();
  }
  readonly trackStatus = (_index: number, status: WorkflowStatusConfiguration['statuses'][number]) => status.code;
  readonly trackRule = (_index: number, rule: WorkflowStatusRule) => rule.id;
  readonly trackStatusUsage = (_index: number, usage: { rule: WorkflowStatusRule }) => usage.rule.id;

  addStatus(): void {
    if (this.disabled || !this.nativeCurrent('labels') || this.nameEditing || this.ruleEditing) return;
    if (this.config.statuses.length >= 50) {
      this.fail('Use at most 50 statuses in this scope.');
      return;
    }
    this.beginNameEdit();
    this.editingCode = null;
    this.nameValues = { label: '' };
    this.markPending();
    this.focusPendingChanges();
  }

  editStatus(code: string): void {
    if (this.disabled || !this.nativeCurrent('labels') || this.nameEditing || this.ruleEditing || this.compactWorkspace && code !== this.selectedStatusCode) return;
    const status = this.config.statuses.find((s) => s.code === code);
    if (!status) return;
    this.editingCode = code;
    this.beginNameEdit();
    this.nameValues = { label: status.label };
    this.markPending();
    this.focusPendingChanges();
  }

  onNameChange(values: unknown, origin?: StatusFormOrigin): void {
    if (!this.nativeCurrent('labels', origin)) { this.nameValues = { ...this.nameValues }; return; }
    if (
      !this.nameEditing ||
      this.disabled ||
      !values ||
      typeof values !== 'object'
    )
      return;
    this.nameValues = {
      label: String((values as { label?: unknown }).label ?? ''),
    };
    this.markPending();
  }

  saveName(): boolean {
    if (this.disabled || !this.nameEditing) return false;
    if (!this.applying && !this.nativeCurrent('labels')) {
      return this.presentationActive() && (!this.compactWorkspace || this.compactView === 'labels')
        ? this.fail('These saved status settings changed while you were editing. Cancel changes and review the latest settings.') : false;
    }
    const label = this.nameValues.label.trim();
    if (!label || label.length > 120 || Array.from(this.nameValues.label).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) >= 127 && character.charCodeAt(0) <= 159))
      return this.fail('Enter a status name of up to 120 characters.');
    if (
      this.config.statuses.some(
        (s) =>
          s.code !== this.editingCode &&
          s.label.trim().toLocaleLowerCase() === label.toLocaleLowerCase(),
      )
    )
      return this.fail('Use a different name for each status in this scope.');
    if (this.editingCode)
      this.config.statuses = this.config.statuses.map((s) =>
        s.code === this.editingCode ? { ...s, label } : s,
      );
    else {
      const code = `status_${crypto.randomUUID().replaceAll('-', '')}`;
      this.config.statuses = [...this.config.statuses, { code, label }];
      this.selectedStatusCode = code;
      if (!this.config.initialStatusCode) this.config.initialStatusCode = code;
    }
    this.nameEditing = false;
    this.editorGeneration++;
    this.editingCode = null;
    this.error = '';
    this.rebuildSettings();
    this.markPending();
    return this.applyImmediately();
  }

  onNameKeydown(event: Event, origin?: StatusFormOrigin): void {
    if (!this.nativeCurrent('labels', origin)) return;
    const key = (event as KeyboardEvent).key;
    if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelName();
    } else if (key === 'Enter') {
      event.preventDefault();
      const code = this.editingCode;
      if (this.saveName()) {
        const savedCode =
          code || this.config.statuses[this.config.statuses.length - 1]?.code;
        if (savedCode) this.focusStatusName(savedCode);
      }
    }
  }

  onNameFocusout(event: FocusEvent, origin?: StatusFormOrigin): void {
    if (!this.nativeCurrent('labels', origin)) return;
    const editor = event.currentTarget as HTMLElement | null;
    if (
      event.relatedTarget instanceof Node &&
      editor?.contains(event.relatedTarget)
    )
      return;
    if (this.immediateChanges && this.nameEditing && this.editingCode)
      this.saveName();
  }

  cancelName(): void {
    if (!this.nameEditing || !this.presentationActive() || this.compactWorkspace && this.compactView !== 'labels') return;
    const code = this.editingCode;
    if (!this.pendingBeforeName) this.reset();
    else {
      this.nameEditing = false;
      this.editingCode = null;
      this.nameValues = { label: '' };
      this.error = this.errorBeforeName;
      this.pending = true;
      this.pendingChangesChange.emit(true);
    }
    this.editorGeneration++;
    if (this.immediateChanges && code) this.focusStatusName(code);
  }

  removeStatus(code: string): void {
    if (this.disabled || !this.nativeCurrent('labels') || this.nameEditing || this.ruleEditing || this.compactWorkspace && code !== this.selectedStatusCode) return;
    if (this.config.rules.some((r) => r.statusCode === code)) {
      if (this.compactWorkspace) {
        const status = this.selectedStatus!, rules = this.selectedStatusRules;
        this.removalRefusal = { code, statusValue: JSON.stringify(status), rulesValue: JSON.stringify(rules),
          message: `${status.label || 'This status'} is used by ${rules.map(rule => this.ruleSummary(rule)).join('; ')}. Change or remove ${rules.length === 1 ? 'that automatic change' : 'those automatic changes'} first.` };
        this.queueFocus(host => host.querySelector<HTMLElement>('[data-status-removal-refusal]'));
        return;
      }
      this.fail(
        'Remove or change the automatic rules using this status first.',
      );
      return;
    }
    if (
      this.config.initialStatusCode === code &&
      this.config.statuses.length > 1
    ) {
      this.fail('Choose another starting status before removing this one.');
      return;
    }
    this.config.statuses = this.config.statuses.filter((s) => s.code !== code);
    if (!this.config.statuses.length) this.config.initialStatusCode = '';
    this.rebuildSettings();
    this.markPending();
    this.applyImmediately();
  }

  onSettingsChange(values: unknown, origin?: StatusFormOrigin): void {
    if (!this.nativeCurrent(undefined, origin)) { this.settingsValues = { ...this.settingsValues }; return; }
    if (this.disabled || this.nameEditing || this.ruleEditing || !values || typeof values !== 'object') return;
    const next = values as Partial<typeof this.settingsValues>;
    const initial =
      typeof next.initialStatusCode === 'string'
        ? next.initialStatusCode
        : this.config.initialStatusCode;
    const manual =
      typeof next.allowManualChanges === 'boolean'
        ? next.allowManualChanges
        : this.config.allowManualChanges;
    if (
      initial === this.config.initialStatusCode &&
      manual === this.config.allowManualChanges
    )
      return;
    this.settingsValues = {
      initialStatusCode: initial,
      allowManualChanges: manual,
    };
    this.config.initialStatusCode = initial;
    this.config.allowManualChanges = manual;
    this.markPending();
    this.applyImmediately();
  }

  addRule(): void {
    if (
      this.disabled ||
      !this.nativeCurrent('changes') ||
      !this.config.statuses.length ||
      this.nameEditing ||
      this.ruleEditing
    )
      return;
    if (this.config.rules.length >= 100) {
      this.fail('Use at most 100 automatic changes in this scope.');
      return;
    }
    this.beginRuleEdit();
    this.editingRuleId = null;
    this.ruleValues = {
      eventCode: this.nodeId === null ? 'workflow_started' : 'step_entered',
      statusCode: this.config.initialStatusCode,
      sourceNodeId: '',
      requirementId: '',
      outcomeCode: '',
    };
    this.rebuildRuleForm();
    this.markPending();
    this.focusPendingChanges();
  }

  editRule(id: string): void {
    if (this.disabled || !this.nativeCurrent('changes') || this.nameEditing || this.ruleEditing || this.compactWorkspace && id !== this.selectedRuleId) return;
    const rule = this.config.rules.find((r) => r.id === id);
    if (!rule) return;
    this.beginRuleEdit();
    this.editingRuleId = id;
    this.ruleValues = {
      eventCode: rule.eventCode,
      statusCode: rule.statusCode,
      sourceNodeId: rule.sourceNodeId ?? '',
      requirementId: rule.requirementId ?? '',
      outcomeCode: rule.outcomeCode ?? '',
    };
    this.rebuildRuleForm();
    this.markPending();
    this.focusPendingChanges();
  }

  onRuleChange(values: unknown, origin?: StatusFormOrigin): void {
    if (!this.nativeCurrent('changes', origin)) { this.ruleValues = { ...this.ruleValues }; return; }
    if (
      this.disabled ||
      !this.ruleEditing ||
      !values ||
      typeof values !== 'object'
    )
      return;
    const fields = values as Record<string, unknown>;
    const next = { ...this.ruleValues };
    for (const key of Object.keys(next) as Array<keyof typeof next>)
      if (typeof fields[key] === 'string') next[key] = fields[key];
    const sourceChanged = next.sourceNodeId !== this.ruleValues.sourceNodeId;
    const eventChanged = next.eventCode !== this.ruleValues.eventCode;
    const requirementChanged =
      next.requirementId !== this.ruleValues.requirementId;
    if (eventChanged) {
      next.requirementId = '';
      next.outcomeCode = '';
      if (next.eventCode.startsWith('workflow_')) next.sourceNodeId = '';
    }
    if (sourceChanged) {
      next.requirementId = '';
      next.outcomeCode = '';
    }
    if (requirementChanged) next.outcomeCode = '';
    this.ruleValues = next;
    this.markPending();
    if (sourceChanged || eventChanged || requirementChanged)
      this.rebuildRuleForm();
  }

  saveRule(): boolean {
    if (this.disabled || !this.document || !this.ruleEditing) return false;
    if (!this.applying && !this.nativeCurrent('changes')) {
      return this.presentationActive() && (!this.compactWorkspace || this.compactView === 'changes')
        ? this.fail('These saved status settings changed while you were editing. Cancel changes and review the latest settings.') : false;
    }
    const rule: WorkflowStatusRule = {
      ...this.config.rules.find((item) => item.id === this.editingRuleId),
      id:
        this.editingRuleId || `rule_${crypto.randomUUID().replaceAll('-', '')}`,
      eventCode: this.ruleValues.eventCode,
      statusCode: this.ruleValues.statusCode,
      sourceNodeId: this.ruleValues.sourceNodeId || null,
      requirementId: this.ruleValues.requirementId || null,
      outcomeCode: this.ruleValues.outcomeCode || null,
    };
    const error = this.ruleIssue(rule);
    if (error) return this.fail(error);
    if (
      rule.eventCode === 'step_outcome_followed' &&
      this.config.rules.some(
        (item) =>
          item.id !== rule.id &&
          item.eventCode === rule.eventCode &&
          item.sourceNodeId === rule.sourceNodeId &&
          item.outcomeCode === rule.outcomeCode,
      )
    )
      return this.fail(
        'Use one automatic workflow status change for each step outcome.',
      );
    this.config.rules = this.editingRuleId
      ? this.config.rules.map((r) => (r.id === this.editingRuleId ? rule : r))
      : [...this.config.rules, rule];
    this.selectedRuleId = rule.id;
    this.ruleEditing = false;
    this.editorGeneration++;
    this.editingRuleId = null;
    this.error = '';
    this.markPending();
    return this.applyImmediately();
  }

  cancelRule(): void {
    if (!this.ruleEditing || !this.presentationActive() || this.compactWorkspace && this.compactView !== 'changes') return;
    if (!this.pendingBeforeRule) this.reset();
    else {
      this.ruleEditing = false;
      this.editingRuleId = null;
      this.error = this.errorBeforeRule;
      this.pending = true;
      this.pendingChangesChange.emit(true);
    }
    this.editorGeneration++;
  }

  removeRule(id: string): void {
    if (this.disabled || !this.nativeCurrent('changes') || this.nameEditing || this.ruleEditing || this.compactWorkspace && id !== this.selectedRuleId) return;
    this.config.rules = this.config.rules.filter((r) => r.id !== id);
    this.markPending();
    this.applyImmediately();
  }
  moveRule(id: string, direction: number): void {
    if (this.disabled || !this.nativeCurrent('changes') || this.nameEditing || this.ruleEditing || this.compactWorkspace && id !== this.selectedRuleId) return;
    const index = this.config.rules.findIndex((r) => r.id === id),
      next = index + direction;
    if (index < 0 || next < 0 || next >= this.config.rules.length) return;
    const rules = [...this.config.rules];
    [rules[index], rules[next]] = [rules[next], rules[index]];
    this.config.rules = rules;
    this.markPending();
    this.applyImmediately();
  }
  ruleSummary(rule: WorkflowStatusRule): string {
    try { return this.readRuleSummary(rule); } catch { return `${this.eventLabel(rule.eventCode)} → ${this.statusLabel(rule.statusCode)} · Unavailable saved source`; }
  }
  eventLabel(code: string): string { return statusEvents.find(event => event.code === code)?.label ?? `Unavailable event (${code})`; }
  sourceLabel(id: string): string {
    const rows = this.currentDocument()?.nodes.filter(node => node?.id === id) ?? [];
    return rows.length === 1 ? rows[0].displayName || rows[0].stepCode || rows[0].id : `Unavailable step (${id})`;
  }
  statusLabel(code: string): string { const rows = this.config.statuses.filter(status => status.code === code); return rows.length === 1 ? rows[0].label : `Unavailable status (${code})`; }
  ruleIssue(rule: WorkflowStatusRule): string {
    try { return this.document ? statusRuleError(this.currentDocument() ?? this.document, this.nodeId, this.config, rule) ?? '' : 'The saved source is unavailable.'; }
    catch { return 'The saved evidence or path source is unavailable.'; }
  }
  private readRuleSummary(rule: WorkflowStatusRule): string {
    const event =
      statusEvents.find((e) => e.code === rule.eventCode)?.label ||
      'Unavailable event';
    const source =
      this.document && rule.sourceNodeId
        ? statusScopeNode(this.document, rule.sourceNodeId)
        : null;
    const step = this.document
      ? statusScopeNode(this.document, this.nodeId || rule.sourceNodeId || '')
      : null;
    const requirements = Array.isArray(step?.declaration?.behavior.completionRequirements)
      ? step.declaration.behavior.completionRequirements.filter(r => r?.id === rule.requirementId) : [];
    const requirement = requirements.length === 1 ? requirements[0] : null;
    const requirementName =
      requirement && step ? statusRequirementLabel(step, requirement) : null;
    const status =
      this.config.statuses.find((s) => s.code === rule.statusCode)?.label ||
      'Unavailable status';
    const outcome =
      rule.eventCode === 'step_outcome_followed' && rule.outcomeCode
        ? (this.document && rule.sourceNodeId
            ? statusStepOutcomeChoices(this.document, rule.sourceNodeId).find(
                (choice) => choice.code === rule.outcomeCode,
              )?.label
            : null) || 'Unavailable outcome'
        : rule.outcomeCode === 'changes_required'
          ? 'Changes required'
          : rule.outcomeCode === 'approved'
            ? 'Approved'
            : null;
    return `${event}${source ? ' · ' + (source.displayName || source.stepCode) : ''}${requirementName ? ' · ' + requirementName : ''}${outcome ? ' · ' + outcome : ''} → ${status}`;
  }

  applyPendingChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    if (!this.pending) return true;
    if (this.disabled) return false;
    const latest = latestDocument || this.currentDocument();
    if (!latest) return false;
    if (
      `${latest.workflowCode}/${latest.definitionVersionCode}/${this.nodeId}/${this.resetVersion}` !==
        this.context ||
      JSON.stringify([latest.workflowCode, latest.definitionVersionCode, latest.packReference]) !== this.baselineReference ||
      JSON.stringify(this.rawConfiguration(latest)) !==
        this.baseline
    )
      return this.fail(
        'These saved status settings changed while you were editing. Cancel changes and review the latest settings.',
      );
    this.applying = true;
    try {
      if (this.nameEditing && !this.saveName()) return false;
      if (this.ruleEditing && !this.saveRule()) return false;
    } finally {
      this.applying = false;
    }
    const error = this.validationError(latest, this.config);
    if (error) return this.fail(error);
    if (
      this.immediateChanges &&
      JSON.stringify(statusConfigurationAt(latest, this.nodeId)) ===
        JSON.stringify(this.config)
    ) {
      this.pending = false;
      this.error = '';
      this.pendingChangesChange.emit(false);
      return true;
    }
    const origin = { nodeId: this.nodeId, owner: this.ownerKey(), source: JSON.stringify(latest), view: this.compactView };
    const next = replaceStatusConfiguration(latest, this.nodeId, this.config);
    const owned: WorkflowStatusOwnedDocumentChange = { document: next, ...origin };
    this.document = next;
    this.baseline = JSON.stringify(statusConfigurationAt(next, this.nodeId));
    this.pending = false;
    this.pendingChangesChange.emit(false);
    this.documentChange.emit(next);
    this.ownedDocumentChange.emit(owned);
    this.message = this.immediateChanges
      ? ''
      : 'Statuses applied locally. Save draft to keep these changes.';
    this.error = '';
    this.rememberSelection();
    return true;
  }
  discardPendingChanges(): void {
    this.document = this.currentDocument(); this.ownerSnapshot = this.ownerKey();
    this.context = `${this.document?.workflowCode}/${this.document?.definitionVersionCode}/${this.nodeId}/${this.resetVersion}`;
    this.reset();
    this.reconcilePresentation();
  }
  hasPendingChanges(): boolean {
    return this.pending;
  }
  /** The status editor owns this buffer independently of route and action input. */
  pendingEditorScopes(
    latestDocument: () => WorkflowDefinitionDocument | null = () => this.document,
  ): readonly WorkflowEditorPendingScope[] {
    return [{
      id: this.nodeId === null ? 'workflow-statuses' : `step-statuses:${this.nodeId}`,
      label: this.nodeId === null ? 'workflow status input' : 'step label input',
      state: (): WorkflowEditorBufferState => {
        if (!this.pending) return 'clean';
        const latest = latestDocument();
        if (!latest || `${latest.workflowCode}/${latest.definitionVersionCode}/${this.nodeId}/${this.resetVersion}` !== this.context ||
          JSON.stringify([latest.workflowCode, latest.definitionVersionCode, latest.packReference]) !== this.baselineReference ||
          !this.ownerCurrent() || JSON.stringify(this.rawConfiguration(latest)) !== this.baseline) return 'stale';
        if (!this.canAuthor || this.interactionBlocked || (this.nodeId !== null && !statusScopeNode(latest, this.nodeId))) return 'invalid';
        try {
          return this.pendingConfigurationError(latest) ? 'invalid' : 'valid';
        } catch { return 'invalid'; }
      },
      fingerprint: () => {
        const latest = latestDocument();
        return JSON.stringify([this.pending, this.baseline, this.baselineReference, this.context, this.resetVersion,
          this.config, this.nameEditing, this.editingCode, this.nameValues,
          this.ruleEditing, this.editingRuleId, this.ruleValues, this.pendingBeforeName, this.pendingBeforeRule,
          this.canAuthor, this.interactionBlocked,
          this.ownerKey(),
          latest && [latest.workflowCode, latest.definitionVersionCode, latest.packReference,
            statusConfigurationAt(latest, this.nodeId), latest.nodes.map(node => [node.id, node.typeCode, node.declaration?.behavior]), latest.edges]]);
      },
      apply: () => this.applyPendingChanges(latestDocument() ?? undefined),
      discard: () => {
        this.document = latestDocument();
        this.discardPendingChanges();
      },
      focus: () => this.focusPendingChanges(),
    }];
  }
  private pendingConfigurationError(document: WorkflowDefinitionDocument): string | null {
    const config = structuredClone(this.config);
    if (this.nameEditing) {
      const label = this.nameValues.label.trim();
      if (!label || label.length > 120 || Array.from(this.nameValues.label).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) >= 127 && character.charCodeAt(0) <= 159)) return 'Enter a status name of up to 120 characters.';
      if (config.statuses.some(status => status.code !== this.editingCode && status.label.trim().toLocaleLowerCase() === label.toLocaleLowerCase()))
        return 'Use a different name for each status in this scope.';
      if (this.editingCode) config.statuses = config.statuses.map(status => status.code === this.editingCode ? { ...status, label } : status);
      else {
        let code = 'pending_status';
        while (config.statuses.some(status => status.code === code)) code += '_';
        config.statuses.push({ code, label });
        if (!config.initialStatusCode) config.initialStatusCode = code;
      }
    }
    if (this.ruleEditing) {
      let id = this.editingRuleId || 'pending_rule';
      while (!this.editingRuleId && config.rules.some(rule => rule.id === id)) id += '_';
      const rule: WorkflowStatusRule = {
        ...config.rules.find(item => item.id === this.editingRuleId),
        id,
        eventCode: this.ruleValues.eventCode,
        statusCode: this.ruleValues.statusCode,
        sourceNodeId: this.ruleValues.sourceNodeId || null,
        requirementId: this.ruleValues.requirementId || null,
        outcomeCode: this.ruleValues.outcomeCode || null,
      };
      config.rules = this.editingRuleId ? config.rules.map(item => item.id === this.editingRuleId ? rule : item) : [...config.rules, rule];
    }
    return this.validationError(document, config);
  }
  private validationError(document: WorkflowDefinitionDocument, config: WorkflowStatusConfiguration): string | null {
    try { return statusConfigurationError(document, this.nodeId, config); }
    catch { return 'The saved evidence or path source is unavailable. Your status input is kept for review.'; }
  }
  focusPendingChanges(reveal = false): void {
    if (this.compactWorkspace) { this.compactView = this.ruleEditing ? 'changes' : this.nameEditing ? 'labels' : this.compactView; this.rememberSelection(); }
    this.queueFocus(host => host.querySelector<HTMLElement>('[role="alert"][tabindex]')
      || host.querySelector<HTMLElement>('[data-status-name-editor] input,[data-status-rule-editor] [aria-invalid="true"],.status-editor__name-input,[aria-label="Status name editor"] input,[aria-label="Automatic status change editor"] input,[aria-label="Automatic status change editor"] mat-select')
      || host.querySelector<HTMLElement>('[data-status-rule-editor] mat-select,input,mat-select,button'), reveal);
  }
  focusHeading(reveal = false): void { this.queueFocus(host => host.querySelector<HTMLElement>('[data-status-heading]'), reveal); }
  focusSelection(): void {
    this.queueFocus(host => Array.from(host.querySelectorAll<HTMLElement>(this.compactView === 'labels' ? '[data-status-label]' : '[data-status-rule]'))
      .find(row => row.getAttribute(this.compactView === 'labels' ? 'data-status-label' : 'data-status-rule') === (this.compactView === 'labels' ? this.selectedStatusCode : this.selectedRuleId))
      ?? host.querySelector<HTMLElement>('[data-status-heading]'));
  }
  /** Open only an exact saved target. Missing/ambiguous paths never start an editor. */
  focusSavedPath(path: string): boolean {
    if (!this.nativeCurrent() || this.retainedIssue || this.nameEditing || this.ruleEditing || typeof path !== 'string') return false;
    let relativePath = path;
    if (this.compactWorkspace) {
      let prefix = '$.statusConfiguration.';
      if (this.nodeId !== null) {
        prefix = `$.nodes[id=${this.nodeId}].statusConfiguration.`;
        const indexed = /^\$\.nodes\[(\d+)\]\.statusConfiguration\./.exec(path), nodes = this.currentDocument()?.nodes;
        if (indexed && Array.isArray(nodes) && nodes[Number(indexed[1])]?.id === this.nodeId
          && nodes.filter(node => node?.id === this.nodeId).length === 1) prefix = indexed[0];
      }
      if (!path.startsWith(prefix)) { this.focusHeading(); return false; }
      relativePath = path.slice(prefix.length);
    }
    const match = (this.compactWorkspace ? /^(statuses|rules)\[(?:(code|id)=)?([^\]]+)\]/
      : /\.statusConfiguration\.(statuses|rules)\[(?:(code|id)=)?([^\]]+)\]/).exec(relativePath);
    if (!match) { this.focusHeading(); return false; }
    if (match[2] && match[2] !== (match[1] === 'statuses' ? 'code' : 'id')) { this.focusHeading(); return false; }
    const saved = this.rawConfiguration(this.currentDocument()) as WorkflowStatusConfiguration | null;
    const rows = match[1] === 'statuses' ? saved?.statuses ?? [] : saved?.rules ?? [];
    const key = match[3].replace(/^['"]|['"]$/g, '');
    const row = !match[2] && /^\d+$/.test(key) ? rows[Number(key)] : rows.find(item => ('code' in item ? item.code : item.id) === key);
    const id = row && ('code' in row ? row.code : row.id);
    const current = match[1] === 'statuses' ? this.config.statuses : this.config.rules;
    if (!id || rows.filter(item => ('code' in item ? item.code : item.id) === id).length !== 1
      || current.filter(item => ('code' in item ? item.code : item.id) === id).length !== 1) { this.focusHeading(); return false; }
    this.inspectionGeneration++; this.statusLabelReturn = null;
    this.compactView = match[1] === 'statuses' ? 'labels' : 'changes';
    if (this.compactView === 'labels') this.selectedStatusCode = id; else this.selectedRuleId = id;
    this.rememberSelection(); this.focusSelection(); return true;
  }
  private queueFocus(target: (host: HTMLElement) => HTMLElement | null, reveal = false): void {
    const request = ++this.focusRequest, owner = this.ownerKey(), source = this.documentKey(this.currentDocument()), generation = this.editorGeneration;
    const selection = JSON.stringify([this.compactView, this.selectedStatusCode, this.selectedRuleId]), focused = this.element.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
      if (request !== this.focusRequest || !this.presentationActive() || owner !== this.ownerKey() || source !== this.documentKey(this.currentDocument())
        || generation !== this.editorGeneration || selection !== JSON.stringify([this.compactView, this.selectedStatusCode, this.selectedRuleId]) || !host.isConnected) return;
      if (active !== focused && active !== host.ownerDocument.body && active !== host.ownerDocument.documentElement && active?.isConnected && !active.closest('[hidden],[inert]')) return;
      const element = target(host);
      if (element && !element.closest('[hidden],[inert]') && !element.matches(':disabled')) {
        const focusedRequest = request + (active === element ? 0 : 1);
        element.focus({ preventScroll: true });
        if (reveal && host.ownerDocument.activeElement === element && focusedRequest === this.focusRequest && this.presentationActive()
          && owner === this.ownerKey() && source === this.documentKey(this.currentDocument()) && generation === this.editorGeneration
          && selection === JSON.stringify([this.compactView, this.selectedStatusCode, this.selectedRuleId]) && element.isConnected
          && !element.closest('[hidden],[inert]')) element.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      }
    });
  }
  @HostListener('focusin') rememberNewFocus(): void { this.focusRequest++; }
  applyVisibleChanges(): void { if (this.presentationActive()) this.applyPendingChanges(); }
  discardVisibleChanges(): void { if (this.presentationActive()) this.discardPendingChanges(); }
  capturePendingStatusInputs(): readonly { name: string; label: string; value: string }[] {
    return !this.pending ? [] : [{ name: `workflow-status-input:${this.nodeId === null ? 'workflow' : encodeURIComponent(this.nodeId)}`,
      label: this.nodeId === null ? 'Unapplied workflow status input' : `Unapplied step labels for ${this.nodeId}`,
      value: JSON.stringify({ format: 'phms.workflow-status-input', version: 1, nodeId: this.nodeId, configuration: this.config, settings: this.settingsValues,
        name: { editing: this.nameEditing, code: this.editingCode, values: this.nameValues, previousPending: this.pendingBeforeName, previousError: this.errorBeforeName },
        rule: { editing: this.ruleEditing, id: this.editingRuleId, values: this.ruleValues, previousPending: this.pendingBeforeRule, previousError: this.errorBeforeRule },
        originalConfiguration: this.baseline, originalReference: this.baselineReference, context: this.context }) }];
  }
  private reset(): void {
    const saved = this.rawConfiguration(this.document);
    this.bufferPresentationKey = this.selectionKey();
    this.editorGeneration++; this.inspectionGeneration++; this.focusRequest++;
    this.removalRefusal = null;
    this.retainedIssue = this.configurationShapeIssue(saved); this.retainedConfiguration = saved;
    this.baseline = JSON.stringify(saved);
    this.baselineReference = JSON.stringify([this.document?.workflowCode, this.document?.definitionVersionCode, this.document?.packReference]);
    this.config = structuredClone(!this.retainedIssue && saved ? saved as WorkflowStatusConfiguration : emptyStatusConfiguration());
    this.nameConfig = [
      {
        ...this.nameConfig[0],
        label:
          this.nodeId === null ? 'Workflow status name' : 'Step label name',
        placeholder:
          this.nodeId === null
            ? 'For example: Returned'
            : 'For example: Checking attachments',
      },
    ];
    this.nameEditing = false;
    this.ruleEditing = false;
    this.editingCode = null;
    this.editingRuleId = null;
    this.error = '';
    this.message = '';
    this.pending = false;
    this.pendingChangesChange.emit(false);
    this.rebuildSettings();
  }
  private markPending(): void {
    if (!this.pending) this.bufferPresentationKey = this.selectionKey();
    this.pending = true;
    this.message = '';
    this.pendingChangesChange.emit(true);
  }
  private beginNameEdit(): void {
    this.editorGeneration++;
    this.pendingBeforeName = this.pending;
    this.errorBeforeName = this.error;
    this.error = '';
    this.nameEditing = true;
  }
  private focusStatusName(code: string): void {
    this.queueFocus(host => this.compactWorkspace
      ? Array.from(host.querySelectorAll<HTMLElement>('[data-status-label]')).find(row => row.getAttribute('data-status-label') === code) ?? null
      : Array.from(host.querySelectorAll<HTMLElement>('[data-status-code]')).find(row => row.getAttribute('data-status-code') === code)?.querySelector<HTMLButtonElement>('.status-editor__name') ?? null);
  }
  private beginRuleEdit(): void {
    this.editorGeneration++;
    this.pendingBeforeRule = this.pending;
    this.errorBeforeRule = this.error;
    this.error = '';
    this.ruleEditing = true;
  }
  private applyImmediately(): boolean {
    return !this.immediateChanges || this.applying
      ? true
      : this.applyPendingChanges();
  }
  private fail(message: string): false {
    this.error = message;
    this.focusPendingChanges();
    return false;
  }
  private rebuildSettings(): void {
    this.settingsValues = {
      initialStatusCode: this.config.initialStatusCode,
      allowManualChanges: this.config.allowManualChanges,
    };
    this.settingsConfig = [
      {
        name: 'initialStatusCode',
        label:
          this.immediateChanges && this.nodeId === null
            ? 'Starting status'
            : this.nodeId === null
              ? 'Starting workflow status'
              : 'Starting step label',
        type: EzFormInputType.SELECT,
        width: '1x',
        options: this.config.statuses.map((s) => ({
          value: s.code,
          viewValue: s.label,
        })),
        validation: [Validators.required],
      },
      {
        name: 'allowManualChanges',
        label:
          this.immediateChanges && this.nodeId === null
            ? 'Allow staff to change the status manually'
            : this.nodeId === null
              ? 'Allow staff to change the workflow status manually'
              : 'Allow staff to change this step label manually',
        type: EzFormInputType.CHECKBOX,
        width: '1x',
      },
    ];
  }
  private rebuildRuleForm(): void {
    try { this.buildRuleForm(); } catch {
      this.ruleConfig = [
        { name: 'eventCode', label: 'When', type: EzFormInputType.SELECT, options: statusEvents.filter(event => this.nodeId === null || event.scope !== 'workflow').map(event => ({ value: event.code, viewValue: event.label })) },
        { name: 'statusCode', label: this.nodeId === null ? 'Set workflow status to' : 'Set step label to', type: EzFormInputType.SELECT, options: this.config.statuses.map(status => ({ value: status.code, viewValue: status.label })) },
      ];
      this.error = 'The saved evidence or path source is unavailable. Your automatic-change input is kept for review.';
    }
  }
  private buildRuleForm(): void {
    const select = (
      name: string,
      label: string,
      options: Array<{ value: string; viewValue: string; disabled?: boolean }>,
      required = true,
    ): InputConfig => {
      const retained = this.ruleValues[name as keyof typeof this.ruleValues];
      const choices = retained && !options.some(option => option.value === retained)
        ? [...options, { value: retained, viewValue: `Unavailable saved value (${retained})`, disabled: true }] : options;
      return {
      name,
      label,
      type: EzFormInputType.SELECT,
      width: '1x',
      options: choices,
      validation: required ? [Validators.required] : [],
    }; };
    const event = this.ruleValues.eventCode;
    const form = [
      select(
        'eventCode',
        'When',
        statusEvents
          .filter((e) => this.nodeId === null || e.scope !== 'workflow')
          .map((e) => ({ value: e.code, viewValue: e.label })),
      ),
    ];
    if (this.nodeId === null && !event.startsWith('workflow_'))
      form.push(
        select(
          'sourceNodeId',
          'In step',
          (
            this.document?.nodes.filter((n) => n.typeCode === 'human_step') ||
            []
          ).map((n) => ({
            value: n.id,
            viewValue: n.displayName || n.stepCode || 'Work step',
          })),
        ),
      );
    const provider = statusRequirementProvider(event);
    if (provider) {
      const step = this.document
        ? statusScopeNode(
            this.document,
            this.nodeId || this.ruleValues.sourceNodeId,
          )
        : null;
      form.push(
        select(
          'requirementId',
          'Requirement',
          (step?.declaration?.behavior.completionRequirements || [])
            .filter((r) => r.provider === provider)
            .map((r) => ({
              value: r.id,
              viewValue: step
                ? statusRequirementLabel(step, r)
                : 'Unavailable requirement',
            })),
        ),
      );
    }
    const decisionStep = this.document
      ? statusScopeNode(
          this.document,
          this.nodeId || this.ruleValues.sourceNodeId,
        )
      : null;
    const outcomeCodes =
      decisionStep?.declaration?.behavior.completionRequirements?.find(
        (r) => r.id === this.ruleValues.requirementId,
      )?.settings['outcomeCodes'];
    if (event === 'decision_recorded')
      form.push(
        select(
          'outcomeCode',
          'Review result',
          [
            { value: '', viewValue: 'Any recorded result' },
            { value: 'approved', viewValue: 'Approved' },
            { value: 'changes_required', viewValue: 'Changes required' },
          ].filter(
            (option) =>
              !option.value ||
              (Array.isArray(outcomeCodes) &&
                outcomeCodes.includes(option.value)),
          ),
          false,
        ),
      );
    if (event === 'step_outcome_followed' && (this.nodeId === null || !this.compactWorkspace))
      form.push(
        select(
          'outcomeCode',
          'Step outcome',
          this.document && this.ruleValues.sourceNodeId
            ? statusStepOutcomeChoices(
                this.document,
                this.ruleValues.sourceNodeId,
              ).map((outcome) => ({
                value: outcome.code,
                viewValue: outcome.label,
              }))
            : [],
        ),
      );
    form.push(
      select(
        'statusCode',
        this.nodeId === null ? 'Set workflow status to' : 'Set step label to',
        this.config.statuses.map((s) => ({
          value: s.code,
          viewValue: s.label,
        })),
      ),
    );
    this.ruleConfig = form;
  }
}
